import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { listAudit, listAuditActions, type AuditEntry } from '../../api/admin';
import { DataTable, EmptyState, ErrorState, LoadingState } from '../ui';

function when(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * What has been done here, newest first.
 *
 * Read-only, on purpose and everywhere: there is no button on this screen, and
 * no endpoint behind it, that edits or removes an entry. A record an
 * administrator can quietly change is not a record.
 */
export function AdminAudit() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [actions, setActions] = useState<{ value: string; label: string }[]>([]);
  const [action, setAction] = useState('');
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async (chosen: string) => {
    try {
      const page = await listAudit(chosen ? { action: chosen } : {});
      setEntries(page.results);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    }
  }, []);

  useEffect(() => {
    listAuditActions()
      .then((answer) => setActions(answer.actions))
      .catch(() => setActions([]));
  }, []);

  useEffect(() => {
    void load(action);
  }, [load, action]);

  if (error) {
    return <ErrorState title="We could not read the record" description={error.message} />;
  }

  return (
    <>
      <label className="sc-admin__inline">
        <span>Show</span>
        <select
          className="sc-admin__select"
          value={action}
          onChange={(event) => setAction(event.target.value)}
        >
          <option value="">Everything</option>
          {actions.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </label>

      {entries === null ? (
        <LoadingState label="Reading the record…" />
      ) : (
        <DataTable
          caption="Administrative decisions"
          rows={entries}
          rowKey={(entry) => entry.id}
          empty={
            <EmptyState
              title="Nothing recorded yet"
              description="Every administrative action is written here as it happens."
            />
          }
          columns={[
            { key: 'when', header: 'When', render: (entry) => when(entry.created_at) },
            {
              key: 'what',
              header: 'What',
              render: (entry) => (
                <>
                  <strong>{entry.action_label}</strong>
                  {(entry.before || entry.after) && (
                    <p className="sc-admin__muted">
                      {entry.before || '—'} → {entry.after || '—'}
                    </p>
                  )}
                </>
              ),
            },
            {
              key: 'who',
              header: 'Who',
              render: (entry) =>
                entry.actor.handle ? (
                  <>
                    @{entry.actor.handle}
                    {!entry.actor.exists && (
                      <span className="sc-admin__muted"> (account gone)</span>
                    )}
                  </>
                ) : (
                  <span className="sc-admin__muted">From the server</span>
                ),
            },
            {
              key: 'target',
              header: 'To what, and why',
              render: (entry) => (
                <>
                  {entry.target || '—'}
                  {entry.reason && <p className="sc-admin__muted">{entry.reason}</p>}
                </>
              ),
            },
          ]}
        />
      )}
    </>
  );
}
